import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { lazy, Suspense } from 'react';
import {
  Search, Truck, MapPin, Clock, Check, Package, ArrowRight, FileText,
  Phone, Mail, Loader2, Download, Camera, User, ChevronDown, ChevronUp, Map
} from 'lucide-react';

const DeliveryMap = lazy(() => import('../components/DeliveryMap'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'booked', label: 'Booked' },
  { key: 'dispatched', label: 'Dispatched' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'in_transit', label: 'In Transit' },
  { key: 'arriving', label: 'Arriving' },
  { key: 'delivered', label: 'Delivered' },
];

const statusColor = (s) => ({
  booked: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  dispatched: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  picked_up: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  in_transit: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  arriving: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  delivered: 'text-green-400 bg-green-400/10 border-green-400/20',
}[s] || 'text-slate-400 bg-slate-400/10 border-slate-400/20');

const stageLabel = (s) => STAGES.find(st => st.key === s)?.label || s;

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CustomerPortal = () => {
  const [identifier, setIdentifier] = useState('');
  const [deliveries, setDeliveries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [storedIdentifier, setStoredIdentifier] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setError('');
    setDeliveries(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('identifier', identifier.trim());
      const res = await axios.post(`${API}/portal/lookup`, formData);
      setDeliveries(res.data);
      setStoredIdentifier(identifier.trim());
    } catch (err) {
      setError(err.response?.data?.detail || 'No deliveries found.');
    } finally {
      setLoading(false);
    }
  };

  const openReceipt = (jobId) => {
    window.open(`${API}/portal/receipt/${jobId}?identifier=${encodeURIComponent(storedIdentifier)}`, '_blank');
  };

  const stageIdx = (status) => STAGES.findIndex(s => s.key === status);
  const activeCount = deliveries?.filter(d => d.status !== 'delivered').length || 0;
  const deliveredCount = deliveries?.filter(d => d.status === 'delivered').length || 0;

  return (
    <div className="min-h-screen" data-testid="customer-portal-page">
      {/* Hero */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-blue/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-2xl mx-auto">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
              MY <span className="text-brand-red">DELIVERIES</span>
            </h1>
            <p className="text-slate-400 text-base sm:text-lg mb-10">
              Enter your email or phone number to view all your deliveries, download receipts, and track active shipments.
            </p>

            <form onSubmit={handleLookup} className="max-w-lg mx-auto" data-testid="portal-lookup-form">
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Email address or phone number"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="flex-1 px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-all text-base"
                  data-testid="portal-identifier-input"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-4 bg-brand-red hover:bg-red-700 text-white font-heading rounded-xl transition-all flex items-center gap-2 disabled:opacity-50"
                  data-testid="portal-lookup-button"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                </button>
              </div>
            </form>

            {error && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="portal-error">
                {error}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Results */}
      {deliveries && (
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Summary */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-brand-surface rounded-xl border border-white/10 p-5" data-testid="portal-stat-total">
                <span className="text-slate-400 text-xs">Total Deliveries</span>
                <p className="text-2xl font-heading text-white">{deliveries.length}</p>
              </div>
              <div className="bg-brand-surface rounded-xl border border-white/10 p-5" data-testid="portal-stat-active">
                <span className="text-slate-400 text-xs">Active</span>
                <p className="text-2xl font-heading text-brand-blue">{activeCount}</p>
              </div>
              <div className="bg-brand-surface rounded-xl border border-white/10 p-5" data-testid="portal-stat-delivered">
                <span className="text-slate-400 text-xs">Completed</span>
                <p className="text-2xl font-heading text-green-400">{deliveredCount}</p>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3 mb-8">
              <Link to="/contact"
                className="flex items-center gap-2 px-5 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-lg font-heading text-sm transition-all"
                data-testid="portal-new-quote-button">
                <FileText className="w-4 h-4" /> REQUEST NEW QUOTE
              </Link>
              <Link to="/track"
                className="flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg text-sm transition-all"
                data-testid="portal-track-link">
                <Truck className="w-4 h-4" /> Track Specific Delivery
              </Link>
            </div>

            {/* Delivery Cards */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="portal-delivery-list">
              {deliveries.map((del, index) => {
                const isExpanded = expandedId === del.id;
                const idx = stageIdx(del.status);
                return (
                  <motion.div
                    key={del.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-brand-surface rounded-xl border border-white/10 overflow-hidden"
                    data-testid={`portal-delivery-${del.id}`}
                  >
                    {/* Card Header (always visible) */}
                    <div className="p-5 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : del.id)}>
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-heading text-brand-red text-lg">{del.id}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColor(del.status)}`}>
                            {stageLabel(del.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); openReceipt(del.id); }}
                            className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white transition-colors"
                            title="Download Receipt"
                            data-testid={`portal-receipt-${del.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                      </div>

                      {/* Mini Progress Bar */}
                      <div className="flex items-center gap-1 mb-3">
                        {STAGES.map((s, i) => (
                          <div key={s.key} className={`h-1.5 flex-1 rounded-full ${i <= idx ? 'bg-brand-red' : 'bg-white/10'}`} />
                        ))}
                      </div>

                      {/* Summary row */}
                      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <MapPin className="w-3.5 h-3.5 text-green-400" />
                          <span className="truncate max-w-[180px]">{del.pickup_address}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <ArrowRight className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <MapPin className="w-3.5 h-3.5 text-brand-red" />
                          <span className="truncate max-w-[180px]">{del.delivery_address}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-500 ml-auto">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{formatDate(del.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="border-t border-white/10 p-5 space-y-5"
                      >
                        {/* Locations Detail */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                              <span className="text-slate-500 text-xs uppercase tracking-wider">Pickup</span>
                            </div>
                            <p className="text-white text-sm">{del.pickup_address}</p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-2.5 h-2.5 rounded-full bg-brand-red" />
                              <span className="text-slate-500 text-xs uppercase tracking-wider">Delivery</span>
                            </div>
                            <p className="text-white text-sm">{del.delivery_address}</p>
                          </div>
                        </div>

                        {/* ETA + Driver */}
                        <div className="flex flex-wrap gap-6 text-sm">
                          {del.eta && (
                            <div className="flex items-center gap-2 text-slate-400">
                              <Clock className="w-4 h-4 text-amber-400" />
                              <span>ETA: {formatDate(del.eta)}</span>
                            </div>
                          )}
                          {del.driver_name && (
                            <div className="flex items-center gap-2 text-slate-400">
                              <User className="w-4 h-4 text-purple-400" />
                              <span>{del.driver_name}</span>
                            </div>
                          )}
                        </div>

                        {/* Notes */}
                        {del.notes && (
                          <div>
                            <span className="text-slate-500 text-xs uppercase tracking-wider">Notes</span>
                            <p className="text-slate-300 text-sm mt-1">{del.notes}</p>
                          </div>
                        )}

                        {/* Live Map */}
                        {del.driver_lat && del.driver_lng && del.status !== 'delivered' && (
                          <div data-testid={`portal-map-${del.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Map className="w-4 h-4 text-brand-red" />
                              <span className="text-slate-500 text-xs uppercase tracking-wider">Live Location</span>
                            </div>
                            <Suspense fallback={<div className="h-[200px] bg-white/5 rounded-xl" />}>
                              <DeliveryMap driverLat={del.driver_lat} driverLng={del.driver_lng}
                                locationUpdatedAt={del.location_updated_at} height="200px" />
                            </Suspense>
                          </div>
                        )}

                        {/* POD */}
                        {del.status === 'delivered' && (del.pod_photo_url || del.pod_signature_url) && (
                          <div className="border-t border-white/10 pt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Check className="w-4 h-4 text-green-400" />
                              <span className="text-green-400 text-xs font-medium uppercase tracking-wider">Proof of Delivery</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {del.pod_photo_url && (
                                <img src={del.pod_photo_url} alt="Delivery proof" className="rounded-lg object-cover max-h-40 w-full border border-white/10" data-testid={`portal-pod-photo-${del.id}`} />
                              )}
                              {del.pod_signature_url && (
                                <img src={del.pod_signature_url} alt="Signature" className="rounded-lg bg-white p-2 max-h-40 w-full object-contain border border-white/10" data-testid={`portal-pod-sig-${del.id}`} />
                              )}
                            </div>
                          </div>
                        )}

                        {/* Status History Timeline */}
                        {del.status_history && del.status_history.length > 0 && (
                          <div className="border-t border-white/10 pt-4">
                            <span className="text-slate-500 text-xs uppercase tracking-wider">Status History</span>
                            <div className="mt-3 space-y-0">
                              {[...del.status_history].reverse().map((entry, i) => (
                                <div key={i} className="flex gap-3">
                                  <div className="flex flex-col items-center">
                                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${i === 0 ? 'bg-brand-red' : 'bg-white/20'}`} />
                                    {i < del.status_history.length - 1 && <div className="w-px h-6 bg-white/10" />}
                                  </div>
                                  <div className="pb-3 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className={`text-sm font-medium ${i === 0 ? 'text-white' : 'text-slate-500'}`}>{stageLabel(entry.status)}</span>
                                      <span className="text-slate-600 text-xs">{formatDate(entry.timestamp)}</span>
                                    </div>
                                    {entry.note && <p className="text-slate-500 text-xs mt-0.5">{entry.note}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* Empty state info */}
      {!deliveries && !loading && !error && (
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Mail, title: 'Email or Phone', desc: 'Enter the email or phone number used when your delivery was booked.' },
                { icon: Package, title: 'View All Deliveries', desc: 'See every delivery — past, active, and completed — in one place.' },
                { icon: Download, title: 'Download Receipts', desc: 'Get branded receipts for completed deliveries, ready to print or save.' },
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="bg-brand-surface rounded-2xl border border-white/10 p-6 text-center">
                  <item.icon className="w-8 h-8 text-brand-red mx-auto mb-4" />
                  <h3 className="font-heading text-base text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CustomerPortal;
